import type { DailyTaskRewardDto } from "../../../api/dto/daily_tasks.dto.js";
import type { PlayerDto } from "../../../api/dto/player.dto.js";
import { adoptEconomyFromPlayer } from "../../../api/player_sync.js";
import { BotGiftService } from "../../../bot_gift/bot_gift_service.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Vector2 } from "../../../common/vector2.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { OverlayId } from "../overlay_ids.js";
import { LevelBoxOpenOverlay } from "./level_box_open_overlay.js";

export interface ReturnGiftOverlayParams
{
  on_closed?: () => void;
  preview_rewards?: DailyTaskRewardDto[];
}

export class ReturnGiftOverlay extends LevelBoxOpenOverlay
{
  private static readonly BOX_H = 337;
  private static readonly LETTER_START_MS = 60;
  private static readonly LETTER_STEP_MS = 20;
  private static readonly LETTER_POP_MS = 80;
  private static readonly LETTER_SETTLE_MS = 70;
  private static readonly LETTER_POP_SCALE = 1.7;
  private static readonly BOX_START_MS = 100;
  private static readonly BOX_POP_MS = 220;
  private static readonly BOX_SETTLE_MS = 140;
  private static readonly BOX_POP_SCALE = 1.12;
  private static readonly HINT_START_MS = 380;
  private static readonly HINT_MS = 260;
  private static readonly HINT_RISE = 40;

  private claimed_player: PlayerDto | null = null;
  private close_notified = false;
  private intro_end_at = 0;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, params: any, overlay_id: OverlayId )
  {
    super( ctx, x, y, width, height, params, overlay_id );

    const now = performance.now();
    this.playBoxIntro( now );
    this.playHintIntro( now );
    this.intro_end_at = now + ReturnGiftOverlay.HINT_START_MS + ReturnGiftOverlay.HINT_MS;
  }

  protected titleKey(): string
  {
    return "RETURN_GIFT.TITLE";
  }

  protected createTitle(): void
  {
    const bounds = this.titleBounds();
    const layers = this.titleLayers();
    const text = LocalizationManager.t( this.titleKey() );
    const chars = Array.from( text );
    const font_size = layers[0].font_size ?? 0;

    this.ctx.save();
    this.ctx.font = `900 ${font_size}px ${TextStyleScheme.rubik}`;
    const metrics = this.ctx.measureText( text );
    const total_w = metrics.width;
    const offset_y = ( metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent ) / 2;
    const starts = chars.map( ( _, i ) => this.ctx.measureText( chars.slice( 0, i ).join( "" ) ).width );
    const widths = chars.map( char => this.ctx.measureText( char ).width );
    this.ctx.restore();

    const now = performance.now();
    const left = bounds.x + ( bounds.w - total_w ) / 2;
    const letter_w = font_size * 2;
    const y = bounds.y + offset_y;

    for ( const layer of layers )
    {
      chars.forEach( ( char, i ) =>
      {
        if ( char.trim() === "" )
          return;

        const x = left + starts[i] + widths[i] / 2 - letter_w / 2;
        const letter = new UITextElement( this.ctx, x, y, letter_w, bounds.h, { ...layer, text: char, auto_wrap: false, using_y_offset: false } );
        letter.setClickable( false );
        this.popLetter( letter, now + ReturnGiftOverlay.LETTER_START_MS + i * ReturnGiftOverlay.LETTER_STEP_MS );
        this.addChild( letter );
      } );
    }
  }

  private popLetter( letter: UITextElement, start: number ): void
  {
    this.pop( letter, start, ReturnGiftOverlay.LETTER_POP_SCALE, ReturnGiftOverlay.LETTER_POP_MS, ReturnGiftOverlay.LETTER_SETTLE_MS );
  }

  private pop( target: UIElement, start: number, peak: number, pop_ms: number, settle_ms: number ): void
  {
    const hidden = new Vector2( 0, 0 );
    const popped = new Vector2( peak, peak );
    const normal = new Vector2( 1, 1 );

    target.setScaleImmediate( hidden );

    const grow = new TransformScale( target, true, hidden, popped, start, pop_ms, { easing: Easing.EaseOutQuad } );
    grow.setOnFinished( () =>
    {
      target.addTransform( new TransformScale( target, true, popped, normal, performance.now(), settle_ms, { easing: Easing.EaseInOutQuad } ) );
    } );
    target.addTransform( grow );
  }

  private playBoxIntro( now: number ): void
  {
    if ( this.box )
      this.pop( this.box, now + ReturnGiftOverlay.BOX_START_MS, ReturnGiftOverlay.BOX_POP_SCALE, ReturnGiftOverlay.BOX_POP_MS, ReturnGiftOverlay.BOX_SETTLE_MS );
  }

  private playHintIntro( now: number ): void
  {
    const hint = this.hint;
    if ( !hint )
      return;

    const start = now + ReturnGiftOverlay.HINT_START_MS;
    const from = new Vector2( 0, LayoutScaler.heightFromDesign( ReturnGiftOverlay.HINT_RISE ) );
    const to = new Vector2( 0, 0 );

    hint.setOpacityImmediate( 0 );
    hint.fadeIn( ReturnGiftOverlay.HINT_MS, start, Easing.EaseOutQuad );
    hint.addTransform( new TransformStraightMove( hint, false, from, to, start, ReturnGiftOverlay.HINT_MS, { easing: Easing.EaseOutQuad } ) );
  }

  public override onTouchStart( p: Vector2, now: number ): boolean
  {
    if ( performance.now() < this.intro_end_at )
      return true;

    return super.onTouchStart( p, now );
  }

  protected closedBoxId(): ImgID
  {
    return ImgID.UI_PinkBox;
  }

  protected openBoxId(): ImgID
  {
    return ImgID.UI_PinkBoxOpen;
  }

  protected boxDesignH(): number
  {
    return ReturnGiftOverlay.BOX_H;
  }

  protected createLevelLabel(): void {}

  protected async openRewards(): Promise<DailyTaskRewardDto[]>
  {
    const preview = ( this.params as ReturnGiftOverlayParams | undefined )?.preview_rewards;
    if ( preview )
      return preview;

    const result = await BotGiftService.claim();
    this.claimed_player = result.player;
    return result.rewards;
  }

  protected applyClaimedRewards(): void
  {
    const player = this.claimed_player;
    this.claimed_player = null;
    if ( player )
      adoptEconomyFromPlayer( player );
  }

  public override onOverlayWillClose(): void
  {
    super.onOverlayWillClose();
    if ( this.close_notified )
      return;
    this.close_notified = true;
    ( this.params as ReturnGiftOverlayParams | undefined )?.on_closed?.();
  }

  public name(): string
  {
    return "ReturnGiftOverlay";
  }
}
